import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { DEFAULT_SKILL_CONTENT } from "~/services/skills";
import {
  SkillEditorForm,
  SkillEditorMode,
  skillValidationSchema,
  type SkillFormData,
  type SkillFormErrors,
} from "./SkillEditorForm";

const MODAL_TITLES: Record<SkillEditorMode, string> = {
  [SkillEditorMode.CREATE]: "Add New Skill",
  [SkillEditorMode.EDIT]: "Edit Skill",
};

interface SkillEditorModalProps {
  errors?: SkillFormErrors;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (data: SkillFormData) => void | Promise<void>;
  open: boolean;
  initialData?: Partial<SkillFormData>;
  mode?: SkillEditorMode;
}

export const SkillEditorModal = ({
  errors = {},
  loading = false,
  onClose,
  onSubmit,
  open,
  initialData,
  mode = SkillEditorMode.CREATE,
}: SkillEditorModalProps) => {
  const {
    control,
    handleSubmit,
    formState: { isValid },
    reset,
  } = useForm<SkillFormData>({
    resolver: yupResolver(skillValidationSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      description: "",
      content: DEFAULT_SKILL_CONTENT,
    },
  });

  // Repopulate when the dialog opens or initialData resolves (Edit fetches the
  // skill's content asynchronously after the modal is already open).
  useEffect(() => {
    if (open) {
      reset({
        name: initialData?.name ?? "",
        description: initialData?.description ?? "",
        content: initialData?.content ?? DEFAULT_SKILL_CONTENT,
      });
    }
  }, [open, initialData, reset]);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:w-[calc(100%_-_64px)] sm:max-w-[900px]">
        <DialogHeader className="shrink-0 px-6 pb-2 pt-6">
          <DialogTitle>{MODAL_TITLES[mode]}</DialogTitle>
        </DialogHeader>

        <SkillEditorForm control={control} errors={errors} loading={loading} mode={mode} />

        <DialogFooter className="shrink-0 px-6 pb-4">
          <Button variant="outline" onClick={onClose} disabled={loading} size="sm">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            disabled={!isValid || loading}
            loading={loading}
            size="sm"
            className="min-w-[72px]"
          >
            {mode === SkillEditorMode.EDIT ? "Save" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
